/**
 * Presell buy box v11 — on-brand tier selector (sections/afroyla-seen-buy.liquid).
 *
 * v13 (07.09.2026, handbag LP — plans/260907-0010-handbag-lp-in-front-buy-black)
 * extends the box for a page that sells PRINTED handbags. Every addition is
 * off unless the section switches it on, so the appliqué page is untouched:
 *   - `data-lp-key`: the permalink carries `attributes[_afl_lp]=<key>` (the
 *     discount function's gate), merged after the ab.js attributes;
 *   - `data-group` on a design block: with two or more groups the rail gets a
 *     row of tab chips (block order, the pick's group open); a page link
 *     `[data-hb-group]` opens that tab on Piece 1;
 *   - `data-families-url` (setting family_forms): assets/afroyla-hb-families.json
 *     is fetched once and, from Piece 2, the rail opens with the chosen
 *     design's other printed forms (packshot · form · price), then an
 *     "Another handbag" tile that reveals the designs. A form is a piece like
 *     any other — same pricing, permalink, receipt, manifest, sticky bar — and
 *     gets a shadow [data-buy-design] node so the detail sheet can find it;
 *   - each tier's free gift defaults to the chosen design's own piece (handle
 *     prefix match) and follows Piece 1 until the shopper picks one herself.
 *
 * v11.1 (05.09.2026) changed only the selected card's BODY COPY in Liquid —
 * a founder line and one verified review replace the sub / reason / FOR run.
 * Nothing here reads or writes that copy; this file is byte-for-byte the v11
 * logic. The picker rows it paints into [data-card-picks] are unchanged.
 *
 * v11 (spec buy-box-v11-on-brand-spec.md) keeps v10's structure and logic and
 * restyles the card in the page's own vocabulary. What changed here:
 *   - the badge is no longer computed. v10 wrote a "pick" and a "best" text
 *     label from pick_card / badge_pick / badge_best; v11 renders one
 *     merchant-set badge_N string per card in Liquid, so this file writes only
 *     the three figures (payable, struck list, saving) and the state;
 *   - the picker label is the page's .afl-seen__label and the low-batch count
 *     is a tag after the ship line — the " · " separator is gone.
 *
 * v10 (spec buy-box-v10-dtc-tier-selector-spec.md) keeps every number and every
 * setting of v8.9 and rebuilds the card:
 *   - the collapsed card is ONE row — count, a small gold TEXT label, the
 *     payable; then the saving and the full list struck. The chips (red and
 *     gold fills) are gone, and so is every figure inside a claim;
 *   - that row is the `role=radio` element, so the pickers are no longer
 *     nested interactives inside a radio. The card wrapper keeps
 *     `data-buy-card` + `aria-checked` because assets/afroyla-seen-sticky.js
 *     reads them to name the set, and that file is not ours to change;
 *   - everything else lives in a body that is a SIBLING of the row and is
 *     `inert` + `aria-hidden` while collapsed, so a card being scanned puts
 *     nothing in the tab order or the accessibility tree.
 *
 * v8.8 (owner 04.09.2026 16:57) removed the summary. Below the cards there is
 * collapsed companions block, the button and the fine print — no YOU SAVE
 * hero, no you-pay line, no because / batch / next-run / loss / ship / quote /
 * places lines. The ship windows and the ≤ threshold batch counts survive
 * INSIDE the selected card, one per row.
 *
 * Three radio cards (1 / 2 / 3 backpacks). The selected card expands into one
 * thumbnail row per unit; ANY design can sit in any row, including the same
 * design twice or three times (owner 04.09.2026 13:31 — the discount ranks
 * UNITS, so two of one design price exactly like two different ones).
 * Everything else — the gallery, the card prices, the total, the button — is
 * one pure function of { units, picks, active, companions }.
 *
 * Every card prints the MONEY it saves against the full list ("SAVE $207.94")
 * under its struck price, recomputed on every repaint from that card's own
 * totals, and hidden outright when the card saves nothing so it can never say
 * "$0.00". The badge is an OWNER string and never a computed one — the page
 * must not say "most chosen", because most real orders are a single backpack.
 *
 * GALLERY (v8.3): the ACTIVE design's real photos always show. Active is the
 * design of the thumbnail row last tapped, or the design of Backpack 1 after a
 * card change. From two backpacks a "your set" packshot strip is moved into
 * the visible gallery under its main frame; tapping a packshot only changes
 * which design the gallery shows — never the card and never a pick.
 *
 * PRICE mirrors the live automatic discount "Appliqué set — faces": the chosen
 * backpack UNITS are ranked by LIST price descending (ties keep pick order,
 * exactly as the function keeps cart order), rank 1 pays rankPct[0],
 * rank 2 rankPct[1], rank 3+ rankPct[2]. Companions are discounted by the
 * NUMBER of backpacks. The percentages are read from the section, never typed
 * here, so the theme and the function are re-synced from one place.
 *
 * The button is a CART PERMALINK (/cart/{variantId}:{qty},…): it replaces the
 * cart with exactly this set and lands on checkout, where the automatic
 * discount applies by itself. Identical variants are aggregated into ONE entry
 * with the summed quantity — the same id twice in a permalink is unreliable.
 * No /discount/ call is made anywhere on this page.
 *
 * 07.09 (owner): a pre-order piece bought from this page MUST carry the same
 * disclosure properties as one bought from the PDP (Pre-order / Ships /
 * _preorder_batch). A permalink cannot carry line properties, so the tap is
 * intercepted and the same set is built through the Cart API instead —
 * /cart/clear.js → /cart/update.js (attributes) → /cart/add.js (items) →
 * /checkout. assets/afroyla-preorder-cart.js wraps window.fetch and decorates
 * the /cart/add body from the registry the section renders
 * (snippets/afroyla-preorder-cart-data per backpack, gift and companion), so
 * this file never names a property. The permalink stays on the href as the
 * fallback when fetch is missing or any step fails.
 */
(function () {
  'use strict';

  if (window.__aflSeenBuyInit) return;
  window.__aflSeenBuyInit = true;

  var MAX_UNITS = 3;
  // v13 — the order a design's other forms are offered in from Piece 2
  var FORM_ORDER = ['Circle crossbody', 'Mini satchel', 'Envelope chain crossbody', 'Tote', 'Backpack'];

  /* "aina-coconut-white-leather-handbag" → "aina-coconut-white": the prefix the
     family JSON is keyed by and the free gifts are matched on. */
  function designPrefix(handle) {
    return String(handle || '').replace(/-leather-handbag$/, '');
  }

  /* A family packshot at one width: "files/x.png" hangs off the CDN root the
     section hands us; a full URL is used as-is. */
  function familyImage(cdn, path, width) {
    var url = /^(https?:)?\/\//.test(path) ? path : cdn + String(path || '').replace(/^\//, '');
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + width;
  }

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  }

  /* Money is never typed: the section hands us the shop's own money_format and
     every figure is a cents attribute rendered by Liquid. */
  function money(cents, format) {
    var fmt = format || '${{amount}}';
    var value = Math.round(cents) / 100;
    function group(n, decimals, thousands, decimal) {
      var fixed = Math.abs(n).toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(parts[1] ? decimal : '');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
  }

  function fill(template, tokens) {
    var out = template || '';
    Object.keys(tokens).forEach(function (key) {
      out = out.split('{' + key + '}').join(tokens[key]);
    });
    return out;
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function pctList(attr, fallback) {
    var parts = String(attr || '')
      .split(',')
      .map(function (n) {
        return toInt(n);
      })
      .filter(function (n) {
        return n >= 0 && n <= 100;
      });
    return parts.length ? parts : fallback;
  }

  /* ab.hc.tools (skill hc-ab-testing): a cart permalink jumps straight to
     checkout, so ab.js never gets to write its cart attributes. Put them on
     the permalink ourselves — the visitor id from its cookie, the running
     assignments from window.__HCAB — so orders attribute to the arm. Empty
     string when no experiment is assigned on this page. */
  function abAttributes() {
    try {
      // union of every assignment this visitor was given (assets/afroyla-hcab-attribution.js)
      var all = typeof window.aflHcabAssignments === 'function' ? window.aflHcabAssignments() : (window.__HCAB && window.__HCAB.assignments) || {};
      var ids = Object.keys(all || {});
      var m = document.cookie.match(/(?:^|;\s*)_hcab_vid=([^;]+)/);
      if (!ids.length || !m) return {};
      var pairs = ids.map(function (id) {
        return id + ':' + all[id];
      });
      return { _ab_visitor: decodeURIComponent(m[1]), _ab_assignments: pairs.join(',') };
    } catch (e) {
      return {};
    }
  }

  /* { key: value } → "?attributes[key]=value&…" for the permalink ('' when empty) */
  function attributesQuery(attrs) {
    var parts = Object.keys(attrs || {}).map(function (key) {
      return 'attributes[' + encodeURIComponent(key) + ']=' + encodeURIComponent(attrs[key]);
    });
    return parts.length ? '?' + parts.join('&') : '';
  }

  /* One POST to the Cart API; resolves only on 2xx so a sold-out add falls back. */
  function cartPost(path, body) {
    return window.fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {}),
    }).then(function (res) {
      if (!res.ok) throw new Error(path + ' ' + res.status);
      return res;
    });
  }

  /* ---- one box ------------------------------------------------------------ */
  function Box(root) {
    var self = this;
    this.root = root;
    this.fmt = root.getAttribute('data-money-format');
    this.rankPct = pctList(root.getAttribute('data-rank-pct'), [50, 65, 75]);
    this.compPct = pctList(root.getAttribute('data-companion-pct'), [50, 55]);
    this.lowThreshold = toInt(root.getAttribute('data-low-threshold')) || 20;
    // v13 — handbag LP hooks (all blank / off on the appliqué page)
    this.lpKey = (root.getAttribute('data-lp-key') || '').trim();
    this.familiesUrl = root.getAttribute('data-families-url') || '';
    this.familiesCdn = root.getAttribute('data-families-cdn') || '';
    this.families = false; // true once the family forms sit in `designs`
    this.tabs = {}; // 'p{slot}' → group tab opened by hand
    this.more = {}; // 'p{slot}' → "Another handbag" revealed by hand

    this.designs = Array.prototype.slice.call(root.querySelectorAll('[data-buy-design]')).map(function (el) {
      var remaining = el.getAttribute('data-remaining');
      return {
        el: el,
        isForm: false,
        parent: -1,
        form: '',
        group: (el.getAttribute('data-group') || '').trim().toLowerCase(),
        groupLabel: (el.getAttribute('data-group-label') || '').trim(),
        handle: el.getAttribute('data-handle') || '',
        name: el.getAttribute('data-name') || '',
        listCents: toInt(el.getAttribute('data-list-cents')),
        variantId: toInt(el.getAttribute('data-variant-id')),
        available: el.getAttribute('data-available') !== 'false',
        window: el.getAttribute('data-window') || '',
        preorder: el.getAttribute('data-preorder') === 'true',
        remaining: remaining === '' || remaining == null ? null : toInt(remaining),
        packSrc: el.getAttribute('data-pack-src') || '',
        packSrcset: el.getAttribute('data-pack-srcset') || '',
        heroSrc: el.getAttribute('data-hero-src') || '',
        gallery: el.querySelector('[data-buy-gallery]'),
      };
    });

    // v13 — the message groups in block order; two or more make a tab row.
    // The chip's label is the first block of the group that names one.
    this.groups = [];
    this.designs.forEach(function (d) {
      if (!d.group) return;
      var found = self.groups.filter(function (g) {
        return g.key === d.group;
      })[0];
      if (!found) self.groups.push({ key: d.group, label: d.groupLabel || d.group.replace(/-/g, ' '), named: !!d.groupLabel });
      else if (!found.named && d.groupLabel) {
        found.label = d.groupLabel;
        found.named = true;
      }
    });

    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-buy-card]'));
    this.companions = Array.prototype.slice.call(root.querySelectorAll('[data-buy-companion]'));
    this.setStrip = root.querySelector('[data-buy-set]');
    this.cta = root.querySelector('[data-buy-checkout]');

    /* v12 — the gift ladder (owner 05.09.2026). One pool per tier, read from
       the hidden [data-gift-pool] lists Liquid rendered; tier t is OPEN while
       t <= units. The shopper's design per tier is giftPicks[t-1]. */
    this.giftTiers = [];
    for (var tier = 1; tier <= MAX_UNITS; tier += 1) {
      var pool = root.querySelector('[data-gift-pool="' + tier + '"]');
      var options = pool
        ? Array.prototype.slice.call(pool.querySelectorAll('[data-gift-option]')).map(function (el) {
            return {
              handle: el.getAttribute('data-handle') || '', // v13 — same-design default
              variantId: toInt(el.getAttribute('data-variant-id')),
              listCents: toInt(el.getAttribute('data-list-cents')),
              name: (el.getAttribute('data-name') || '').trim(),
              short: (el.getAttribute('data-short') || '').trim(),
              available: el.getAttribute('data-available') !== 'false',
              // 06.09 — the piece's OWN ship status (snippets/afroyla-seen-ship-attrs)
              preorder: el.getAttribute('data-preorder') === 'true',
              window: el.getAttribute('data-window') || '',
              remaining: el.getAttribute('data-remaining') ? toInt(el.getAttribute('data-remaining')) : null,
              src: el.getAttribute('data-src') || '',
              srcset: el.getAttribute('data-srcset') || '',
              // v12.9 — the piece's own photos (900 / 400), for the gift gallery
              images: (el.getAttribute('data-images') || '').split('|').filter(Boolean),
              thumbs: (el.getAttribute('data-thumbs') || '').split('|').filter(Boolean),
            };
          })
        : [];
      this.giftTiers.push({ tier: tier, name: pool ? pool.getAttribute('data-gift-name') || '' : '', options: options });
    }
    this.rail = root.querySelector('[data-gift-rail]');
    // v12.9 — the shared gallery a tapped FREE piece opens in; `activeGift` is
    // the tier index (0-based) on show, or null while a backpack is on show
    this.giftGallery = root.querySelector('[data-gift-gallery]');
    this.activeGift = null;
    this.paintedGift = null;
    // tiers (0-based) that just opened — they animate once on the next paint
    this.unlocking = [];

    /* 06.09 22:37 (owner: "sợ dài quá … test thử") — COMPACT PICKS. Backpack
       1 always shows its full design rail (the main decision); Backpack 2–3
       and every gift row render as one "chosen" line (thumb · name · status ·
       Change) until the shopper taps Change, which opens that row's rail in
       place; a pick closes it again. `open` holds the rows opened by hand,
       keyed 'p{slot}' / 'g{tierIndex}'. Section setting `compact_picks`. */
    this.compact = root.getAttribute('data-compact') === 'true';
    this.open = {};

    this.units = Math.min(MAX_UNITS, Math.max(1, toInt(root.getAttribute('data-default-units')) || 1));
    this.picks = this.startingPicks(root.getAttribute('data-default-picks'));
    // which design the gallery is showing — Backpack 1 until the shopper taps
    this.active = this.picks[0];
    // v13 — each tier's gift opens on the chosen design's own piece when the
    // pool has one, else the first available; a tier the shopper touched stays.
    this.giftTouched = [];
    this.giftPicks = this.defaultGiftPicks();
    this.loadFamilies();
  }

  /* Every slot is pre-filled, so no row is ever empty: the merchant's handles
     first, then any design not already taken. */
  Box.prototype.startingPicks = function (attr) {
    var self = this;
    var wanted = String(attr || '')
      .split(',')
      .map(function (h) {
        return h.trim();
      })
      .filter(Boolean);
    this.wanted = wanted; // v13 — re-read once the family forms exist
    var picks = [];
    wanted.forEach(function (handle) {
      var i = self.designs.findIndex(function (d) {
        return d.handle === handle;
      });
      if (i >= 0 && picks.indexOf(i) < 0) picks.push(i);
    });
    for (var i = 0; i < this.designs.length && picks.length < MAX_UNITS; i += 1) {
      if (picks.indexOf(i) < 0) picks.push(i);
    }
    return picks;
  };

  /* ---- v13: the free gift defaults to the chosen design ------------------- */
  Box.prototype.defaultGiftIndex = function (tierIndex) {
    var tier = this.giftTiers[tierIndex];
    var lead = this.designs[this.picks[0]];
    var prefix = lead ? designPrefix(lead.handle) : '';
    var first = -1;
    var same = -1;
    tier.options.forEach(function (o, i) {
      if (!o.available) return;
      if (first < 0) first = i;
      if (same < 0 && prefix && o.handle.indexOf(prefix + '-') === 0) same = i;
    });
    if (same >= 0) return same;
    return first >= 0 ? first : 0;
  };

  Box.prototype.defaultGiftPicks = function () {
    var self = this;
    return this.giftTiers.map(function (t, i) {
      return self.defaultGiftIndex(i);
    });
  };

  /* Piece 1 changed design: every gift row the shopper has not touched moves
     to the new design's piece (a no-op where the pool has none). */
  Box.prototype.refreshGiftDefaults = function () {
    var self = this;
    this.giftTiers.forEach(function (t, i) {
      if (!self.giftTouched[i]) self.giftPicks[i] = self.defaultGiftIndex(i);
    });
  };

  /* ---- v13: the family forms (assets/afroyla-hb-families.json) ------------ */
  Box.prototype.loadFamilies = function () {
    var self = this;
    if (!this.familiesUrl || typeof window.fetch !== 'function') return;
    window
      .fetch(this.familiesUrl)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (json) {
        if (json && json.designs) self.addFamilies(json);
      })
      .catch(function () {});
  };

  /* One shadow [data-buy-design] per in-stock form of every design — the data
     the rail, the pricing and the detail sheet read (name, note and meaning
     are the parent design's, the facts line is the form's), plus a one-photo
     gallery so the active piece is always the one on show. */
  Box.prototype.addFamilies = function (json) {
    var self = this;
    var cdn = this.familiesCdn;
    if (!cdn) {
      // the JSON names its own CDN root in its note
      var m = String(json._ || '').match(/https?:\/\/cdn\.shopify\.com\/\S*\//);
      cdn = m ? m[0] : '';
    }
    var factsEl = this.root.querySelector('[data-family-facts]');
    var facts = {};
    try {
      facts = (factsEl && JSON.parse(factsEl.textContent)) || {};
    } catch (e) {
      facts = {};
    }
    var host = this.root.querySelector('.afl-buy__designs') || (this.designs[0] && this.designs[0].el.parentNode);
    if (!host) return;
    var added = 0;
    this.designs.slice().forEach(function (d, parentIndex) {
      if (d.isForm) return;
      var forms = (json.designs[designPrefix(d.handle)] || []).slice();
      forms.sort(function (a, b) {
        var ia = FORM_ORDER.indexOf(a.t);
        var ib = FORM_ORDER.indexOf(b.t);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
      forms.forEach(function (f) {
        if (!f.h || !f.v || !f.t) return;
        var name = d.name + ' ' + f.t;
        var hero = familyImage(cdn, f.i, 900);
        var el = document.createElement('div');
        el.className = 'afl-buy-design afl-buy-design--form';
        el.setAttribute('data-buy-design', '');
        el.setAttribute('data-buy-form', f.t);
        el.setAttribute('data-handle', f.h);
        el.setAttribute('data-name', name);
        el.setAttribute('data-meaning', d.el.getAttribute('data-meaning') || '');
        el.setAttribute('data-note', d.el.getAttribute('data-note') || '');
        el.setAttribute('data-facts', facts[f.t] || '');
        el.setAttribute('data-list-cents', String(toInt(f.c)));
        el.setAttribute('data-variant-id', String(toInt(f.v)));
        el.setAttribute('data-available', 'true');
        el.setAttribute('data-preorder', 'false');
        el.setAttribute('data-active', 'false');
        el.innerHTML =
          '<div class="afl-buy__gal" data-buy-gallery hidden><div class="afl-buy__frame"><img class="afl-buy__main" data-buy-main src="' +
          esc(hero) +
          '" alt="' +
          esc(name + ' — photo 1') +
          '" loading="lazy" decoding="async" width="900" height="1200"></div><div class="afl-buy__thumbs"><button type="button" class="afl-buy__thumb" data-buy-thumb data-hc-el="gallery-thumb" data-main-src="' +
          esc(hero) +
          '" data-main-srcset="" data-main-alt="' +
          esc(name + ' — photo 1') +
          '" aria-label="' +
          esc(name + ' — photo 1 of 1') +
          '" aria-current="true"><img src="' +
          esc(familyImage(cdn, f.i, 400)) +
          '" alt="" loading="lazy" decoding="async" width="400" height="400"></button></div></div>';
        host.appendChild(el);
        self.designs.push({
          el: el,
          isForm: true,
          parent: parentIndex,
          form: f.t,
          group: d.group,
          groupLabel: '',
          handle: f.h,
          name: name,
          listCents: toInt(f.c),
          variantId: toInt(f.v),
          available: true,
          window: '',
          preorder: false,
          remaining: null,
          packSrc: familyImage(cdn, f.i, 240),
          packSrcset: familyImage(cdn, f.i, 480) + ' 2x',
          heroSrc: hero,
          gallery: el.querySelector('[data-buy-gallery]'),
        });
        added += 1;
      });
    });
    if (!added) return;
    this.families = true;
    this.seedFamilyPicks();
    this.render();
  };

  /* Piece 1 keeps its design; the merchant's named handles (designs or forms)
     fill the next slots, then the chosen design's own forms, then designs. */
  Box.prototype.seedFamilyPicks = function () {
    var self = this;
    var picks = [this.picks[0]];
    (this.wanted || []).forEach(function (handle) {
      var i = self.designs.findIndex(function (d) {
        return d.handle === handle;
      });
      if (i >= 0 && picks.indexOf(i) < 0 && picks.length < MAX_UNITS) picks.push(i);
    });
    var forms = this.formsOf(picks[0]);
    for (var k = 0; k < forms.length && picks.length < MAX_UNITS; k += 1) {
      if (picks.indexOf(forms[k]) < 0) picks.push(forms[k]);
    }
    for (var j = 0; j < this.designs.length && picks.length < MAX_UNITS; j += 1) {
      if (!this.designs[j].isForm && picks.indexOf(j) < 0) picks.push(j);
    }
    this.picks = picks;
  };

  /* The indexes of one design's forms, in FORM_ORDER. */
  Box.prototype.formsOf = function (index) {
    var out = [];
    this.designs.forEach(function (d, i) {
      if (d.isForm && d.parent === index) out.push(i);
    });
    return out;
  };

  /* Piece 1 changed design: a slot holding one of the OLD design's forms takes
     the same form of the new design (its first form when that one is missing,
     what it held when the new design has no forms at all). */
  Box.prototype.followForms = function (prev, next) {
    var self = this;
    var forms = this.formsOf(next);
    for (var s = 1; s < MAX_UNITS; s += 1) {
      var d = this.designs[this.picks[s]];
      if (!d || !d.isForm || d.parent !== prev) continue;
      var same = forms.filter(function (i) {
        return self.designs[i].form === d.form;
      })[0];
      if (same != null) this.picks[s] = same;
      else if (forms.length) this.picks[s] = forms[0];
    }
  };

  /* Rank pricing per UNIT — the same rule the discount function runs on the
     cart. A design chosen twice simply occupies two consecutive ranks. */
  Box.prototype.priceBackpacks = function (items) {
    var self = this;
    var order = items.map(function (item, i) {
      return { item: item, i: i };
    });
    order.sort(function (a, b) {
      return b.item.listCents - a.item.listCents || a.i - b.i;
    });
    var out = items.map(function () {
      return null;
    });
    order.forEach(function (entry, position) {
      var rank = Math.min(position + 1, self.rankPct.length);
      var pct = self.rankPct[rank - 1];
      out[entry.i] = {
        design: entry.item,
        rank: rank,
        pct: pct,
        listCents: entry.item.listCents,
        payCents: entry.item.listCents - Math.floor((entry.item.listCents * pct) / 100),
      };
    });
    return out;
  };

  Box.prototype.chosen = function (units) {
    var self = this;
    return this.picks.slice(0, units == null ? this.units : units).map(function (i) {
      return self.designs[i];
    });
  };

  Box.prototype.companionPct = function () {
    return this.units >= 2 ? this.compPct[1] : this.compPct[0];
  };

  Box.prototype.tickedCompanions = function () {
    var pct = this.companionPct();
    return this.companions
      .filter(function (input) {
        return input.checked;
      })
      .map(function (input) {
        var listCents = toInt(input.getAttribute('data-list-cents'));
        return {
          input: input,
          // some catalogue titles carry a trailing space ("Chanua" Bloom Charm )
          name: (input.getAttribute('data-name') || '').trim(),
          variantId: toInt(input.getAttribute('data-variant-id')),
          preorder: input.getAttribute('data-preorder') === 'true',
          window: input.getAttribute('data-window') || '',
          remaining: input.getAttribute('data-remaining') ? toInt(input.getAttribute('data-remaining')) : null,
          listCents: listCents,
          pct: pct,
          payCents: listCents - Math.floor((listCents * pct) / 100),
        };
      });
  };

  /* ---- gifts (v12) --------------------------------------------------------
     The ladder is CUMULATIVE: with N backpacks every tier t <= N is open and
     contributes ONE piece at $0. The list value of the open gifts joins the
     card's struck total, so "SAVE $x" counts what she actually receives; the
     payable never moves. Mirrors the discount function's gift rule. */
  Box.prototype.giftLines = function (units) {
    var self = this;
    var n = units == null ? this.units : units;
    var out = [];
    this.giftTiers.forEach(function (t, i) {
      if (i >= n || !t.options.length) return;
      var option = t.options[self.giftPicks[i]] || t.options[0];
      out.push({ tier: t.tier, index: i, name: t.name, option: option, listCents: option.listCents, payCents: 0 });
    });
    return out;
  };

  Box.prototype.giftListCents = function (units) {
    return this.giftLines(units).reduce(function (sum, g) {
      return sum + g.listCents;
    }, 0);
  };

  /* "charm" · "charm + wallet" · "charm, wallet + circle bag" */
  Box.prototype.giftNamesText = function (units) {
    var names = this.giftLines(units).map(function (g) {
      return g.name;
    });
    if (!names.length) return '';
    if (names.length === 1) return names[0];
    return names.slice(0, -1).join(', ') + ' + ' + names[names.length - 1];
  };

  Box.prototype.giftThumbsHtml = function (tierIndex) {
    var self = this;
    var tier = this.giftTiers[tierIndex];
    return tier.options
      .map(function (o, i) {
        var on = self.giftPicks[tierIndex] === i;
        var blocked = !o.available;
        return (
          '<button type="button" class="afl-buy__th' +
          (on ? ' is-on' : '') +
          (blocked ? ' is-off' : '') +
          '" data-hc-el="x-gift-pick" data-gift-tier="' +
          tierIndex +
          '" data-gift-index="' +
          i +
          '" role="radio" aria-checked="' +
          (on ? 'true' : 'false') +
          '"' +
          (blocked ? ' aria-disabled="true"' : '') +
          ' tabindex="' +
          (on ? '0' : '-1') +
          '"><span class="afl-buy__th-well"><img src="' +
          esc(o.src) +
          '" srcset="' +
          esc(o.srcset) +
          '" alt="" loading="lazy" decoding="async" width="48" height="48"></span><span class="afl-buy__th-n">' +
          esc(o.short) +
          '</span></button>'
        );
      })
      .join('');
  };

  /* One label-headed picker per OPEN tier, after the backpack rows. */
  Box.prototype.giftRowsHtml = function () {
    var self = this;
    var rowTpl = this.root.getAttribute('data-gift-row-label') || 'Your {gift}';
    var onUs = this.root.getAttribute('data-gift-on-us') || 'On us';
    var lineTpl = this.root.getAttribute('data-gift-ship') || '{name} — on us';
    return this.giftLines()
      .map(function (g) {
        var label = fill(rowTpl, { gift: g.name });
        var reveal = self.unlocking.indexOf(g.index) >= 0;
        var folded = self.isFolded('g' + g.index, true);
        if (folded) label = fill(self.root.getAttribute('data-gift-row-label-compact') || 'Your free {gift}', { gift: g.name });
        // "$44.95 Asili Leather Charm — free · ● In stock · ships now": the
        // gift's OWN status, never the backpack's box (owner 06.09 19:23).
        var status = dotHtml(g.option.preorder) + esc(self.statusTextFor(g.option));
        var line =
          '<p class="afl-buy__gift-line"><s>' +
          esc(money(g.listCents, self.fmt)) +
          '</s> ' +
          esc(fill(lineTpl, { name: g.option.name })) +
          ' · ' +
          status +
          '</p>';
        return (
          '<div class="afl-buy__gift-row' +
          (reveal ? ' is-reveal' : '') +
          (folded ? ' is-compact' : '') +
          '"><span class="afl-seen__label afl-buy__gift-lbl">' +
          esc(label) +
          '</span>' +
          (folded ? self.chosenHtml('g' + g.index, g.option.src, g.option.srcset, g.option.name, '<s>' + esc(money(g.listCents, self.fmt)) + '</s> ' + esc(self.root.getAttribute('data-free-tag') || 'Free') + ' · ' + status, self.viewButtonHtml()) : '') +
          '<div class="afl-buy__gift-thumbs" role="radiogroup" aria-label="' +
          esc(label) +
          '"' +
          (folded ? ' hidden' : '') +
          '>' +
          self.giftThumbsHtml(g.index) +
          '</div>' +
          (folded ? '' : line + self.viewButtonHtml()) +
          '</div>'
        );
      })
      .join('');
  };

  /* v12.9 (owner 06.09: "mỗi lần click xong phải scroll lên gallery cũng
     phiền … kiểu view details") — a text link under every picker row's ship
     line; assets/afroyla-seen-detail-sheet.js opens the sheet for the row's
     current pick. The label is the section's sheet_view_label. */
  Box.prototype.viewButtonHtml = function () {
    var copy = this.root.querySelector('[data-sheet-copy]');
    var label = '';
    if (copy) {
      try {
        label = JSON.parse(copy.textContent).view || '';
      } catch (e) {
        label = '';
      }
    }
    if (!label) return '';
    return '<button type="button" class="afl-buy__view" data-buy-view data-hc-el="quick-view-open">' + esc(label) + '</button>';
  };

  /* v12.1 — the MYSTERY GIFTS strip inside each card row (owner 05.09.2026
     20:34): one box per gift the tier opens. Sealed on an unselected row —
     an ink gift-box glyph on the well and "2 mystery gifts inside"; on the
     selected row every box is REVEALED: the chosen piece's packshot, the
     caption "charm + wallet — on us". Selecting a row runs the reveal once
     (the box flips into the piece, the gold tag pops). */
  /* v12.2 (owner 21:49): EVERY row shows all three gifts. A slot whose tier the
     row covers is INCLUDED — a sealed box on an unselected row, the revealed
     piece with a FREE stamp on the selected one; a slot above the tier is
     LOCKED (padlock, "Unlocks with N"). The caption says the count and the
     dollar value in ink, the gold scope line, and — on the selected card — the
     pull to the next tier ("Add a 2nd backpack → the wallet is free too"). */
  var SVG_BOX =
    '<svg class="afl-buy__mg-box" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
    '<g class="afl-buy__mg-lid"><path d="M3 6.5h18V9.5H3z"></path><path d="M12 6.5c-2.6 0-4.5-1.2-4.5-2.6C7.5 2.7 9 2.3 10 3c1 .7 2 3.5 2 3.5s1-2.8 2-3.5c1-.7 2.5-.3 2.5.9 0 1.4-1.9 2.6-4.5 2.6"></path></g>' +
    '<g class="afl-buy__mg-base"><rect x="4" y="9.5" width="16" height="11"></rect><path d="M12 9.5v11"></path></g></svg>';
  var SVG_LOCK =
    '<svg class="afl-buy__mg-padlock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
    '<rect x="5" y="10.5" width="14" height="10"></rect><path class="afl-buy__mg-shackle" d="M8 10.5V7.5a4 4 0 0 1 8 0v3"></path></svg>';

  /* v12.3 (owner 22:03): a sealed or locked slot never names its piece — the
     gift is a MYSTERY until the row is chosen. Art: the merchant's still-life
     images (sealed box / padlock / open box) when set, the ink glyphs otherwise.
     The caption sits UNDER the tiles, two lines at most: the value line in ink,
     then on the selected row the gold pull to the next pack. */
  function artImg(cls, src) {
    return src ? '<img class="' + cls + '" src="' + esc(src) + '" alt="" loading="lazy" decoding="async" width="56" height="56">' : '';
  }

  Box.prototype.rowGiftsHtml = function (units, on, revealing) {
    var self = this;
    var root = this.root;
    var tiers = this.giftTiers.filter(function (t) {
      return t.options.length;
    });
    if (!tiers.length) return '';
    var included = this.giftLines(units);
    var value = included.reduce(function (sum, g) {
      return sum + g.listCents;
    }, 0);
    var freeTag = root.getAttribute('data-free-tag') || 'Free';
    var mysterySub = root.getAttribute('data-mystery-sub') || 'Mystery';
    var openSubTpl = root.getAttribute('data-open-sub') || 'Free {gift}';
    // v12.6 (owner 06.09 01:24): a label ABOVE the tiles so the strip reads as
    // gifts that come with the set, never as three items being bought
    var stripLabel = root.getAttribute('data-strip-label') || '';
    var lockedTpl = root.getAttribute('data-locked-tile') || 'Unlocks with {n}';
    var boxSrc = root.getAttribute('data-gift-box-src') || '';
    var lockSrc = root.getAttribute('data-gift-lock-src') || '';
    var openSrc = root.getAttribute('data-gift-open-src') || '';
    var valueTpl = on
      ? included.length === 1
        ? root.getAttribute('data-value-one') || 'Unlocked: 1 gift worth {value}, free'
        : root.getAttribute('data-value-many') || 'Unlocked: {n} gifts worth {value}, free'
      : included.length === 1
        ? root.getAttribute('data-sealed-one') || '1 free mystery gift inside · worth {value}'
        : root.getAttribute('data-sealed-many') || '{n} free mystery gifts inside · worth {value}';
    var scope = root.getAttribute('data-free-scope') || '';
    var nextTier = tiers[units]; // the first tier this row does not cover
    var hintTpl = nextTier ? root.getAttribute('data-next-hint-' + nextTier.tier) || '' : '';

    var tiles = tiers
      .map(function (t, i) {
        var isIn = i < units;
        var option = t.options[self.giftPicks[i]] || t.options[0];
        var state = !isIn ? 'is-locked' : on ? 'is-open' : 'is-sealed';
        var unlocking = on && revealing && self.unlocking.indexOf(i) >= 0;
        // v12.5 (owner 06.09 01:05): an open slot says the KIND, never the piece — "Free leather charm"
        var sub = !isIn ? fill(lockedTpl, { n: t.tier }) : on ? fill(openSubTpl, { gift: t.name }) : mysterySub;
        return (
          '<span class="afl-buy__mg-slot ' +
          state +
          (on && isIn && revealing ? ' is-revealing' : '') +
          (unlocking ? ' is-unlocking' : '') +
          (boxSrc ? ' has-art' : '') +
          '" style="--afl-mg-i:' +
          i +
          '" data-mg-slot="' +
          i +
          '"><span class="afl-buy__mg-tile">' +
          (boxSrc ? artImg('afl-buy__mg-art afl-buy__mg-art--box', boxSrc) : SVG_BOX) +
          (lockSrc ? artImg('afl-buy__mg-art afl-buy__mg-art--lock', lockSrc) : SVG_LOCK) +
          (openSrc ? artImg('afl-buy__mg-art afl-buy__mg-art--open', openSrc) : '') +
          '<img class="afl-buy__mg-img" src="' +
          esc(option.src) +
          '" srcset="' +
          esc(option.srcset) +
          '" alt="" loading="lazy" decoding="async" width="48" height="48">' +
          '</span><span class="afl-buy__mg-free">' +
          esc(freeTag) +
          '</span>' +
          '<span class="afl-buy__mg-sub">' +
          esc(sub) +
          '</span></span>'
        );
      })
      .join('');

    var line1 = fill(valueTpl, { n: included.length, value: money(value, self.fmt) });
    if (on && scope) line1 += ' · ' + scope;
    var caption =
      '<span class="afl-buy__mg-cap' +
      (on ? ' is-open' : '') +
      (on && revealing ? ' is-revealing' : '') +
      '"><span class="afl-buy__mg-value">' +
      esc(line1) +
      '</span>' +
      (on && nextTier && hintTpl ? '<span class="afl-buy__mg-hint">' + esc(fill(hintTpl, { gift: nextTier.name })) + '</span>' : '') +
      '</span>';

    return (
      (stripLabel ? '<span class="afl-buy__mg-lbl">' + esc(stripLabel) + '</span>' : '') +
      '<span class="afl-buy__mg-tiles">' +
      tiles +
      '</span>' +
      caption
    );
  };

  /* The reveal's particle burst: a small gold flurry from each revealed slot,
     one after the other, via canvas-confetti (assets/afroyla-confetti.min.js)
     when it is present. Never on reduced motion. */
  Box.prototype.fireReveal = function () {
    if (typeof window.confetti !== 'function') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var slots = this.root.querySelectorAll('[data-buy-card][aria-checked="true"] .afl-buy__mg-slot.is-revealing');
    slots.forEach(function (slot, i) {
      var unlocking = slot.classList.contains('is-unlocking');
      window.setTimeout(function () {
        var r = slot.querySelector('.afl-buy__mg-tile').getBoundingClientRect();
        if (!r.width) return;
        window.confetti({
          particleCount: unlocking ? 34 : 22,
          spread: 58,
          startVelocity: 17,
          gravity: 1.1,
          decay: 0.9,
          ticks: 90,
          scalar: 0.62,
          shapes: ['square', 'circle'],
          colors: ['#a9894c', '#d8c48e', '#030405', '#ffffff'],
          origin: { x: (r.left + r.width / 2) / window.innerWidth, y: (r.top + r.height / 2) / window.innerHeight },
          zIndex: 40,
          disableForReducedMotion: true,
        });
      }, 420 + i * 140 + (unlocking ? 380 : 0));
    });
  };

  /* v12.7 — the chosen backpacks pictured in the row, under an ink label
     ("Your backpacks"). v12.9 (owner picked "B"): one tile per DESIGN, and a
     design taken twice wears a "×2" pip instead of repeating its tile; the
     connectors between tiles are CSS (afroyla-seen-gifts.css). */
  Box.prototype.rowBagsHtml = function (units) {
    var self = this;
    var label = this.root.getAttribute('data-bags-label') || '';
    var tiles = this.groupedChosen(units)
      .map(function (group) {
        var d = group.design;
        return (
          '<span class="afl-buy__bg-slot"><span class="afl-buy__bg-tile"><img src="' +
          esc(d.packSrc) +
          '" srcset="' +
          esc(d.packSrcset) +
          '" alt="" loading="lazy" decoding="async" width="48" height="48">' +
          (group.count > 1 ? '<span class="afl-buy__bg-pip">×' + group.count + '</span>' : '') +
          '</span><span class="afl-buy__bg-n">' +
          esc(d.name) +
          '</span></span>'
        );
      })
      .join('');
    return (label ? '<span class="afl-buy__bg-lbl">' + esc(label) + '</span>' : '') + '<span class="afl-buy__bg-tiles">' + tiles + '</span>';
  };

  /* Kept as a no-op hook: the rail was replaced by the in-row strips. */
  Box.prototype.paintRail = function () {};

  Box.prototype.setGift = function (tierIndex, index) {
    var tier = this.giftTiers[tierIndex];
    if (!tier || !tier.options[index] || this.giftPicks[tierIndex] === index) return;
    this.giftPicks[tierIndex] = index;
    this.giftTouched[tierIndex] = true; // v13 — never re-defaulted after a pick
    delete this.open['g' + tierIndex]; // a pick folds the row again
    // v12.9 — the gift she just chose is the one the gallery shows
    if (this.giftGallery && tierIndex < this.units) this.activeGift = tierIndex;
    this.render();
  };

  /* ---- painting ----------------------------------------------------------- */
  /* One rail tile. v13: a family form names its FORM on the tile and keeps the
     design's name in a visually-hidden span, so the tile's text — which the
     sticky bar and the detail sheet read as the piece's name — stays the full
     "Aina Coconut White Tote"; `priced` prints what the tile would add to the
     total (its list struck above it), by the same rank rule as the card. */
  Box.prototype.tileHtml = function (slot, i, priced) {
    var d = this.designs[i];
    var on = this.picks[slot] === i;
    var blocked = !d.available;
    var label = d.isForm ? '<span class="afl-buy__sr">' + esc(this.designs[d.parent].name + ' ') + '</span>' + esc(d.form) : esc(d.name);
    var price = '';
    if (priced) {
      var pay = this.marginalCents(slot, i);
      price = '<span class="afl-buy__th-p">' + (pay < d.listCents ? '<s>' + esc(money(d.listCents, this.fmt)) + '</s>' : '') + esc(money(pay, this.fmt)) + '</span>';
    }
    return (
      '<button type="button" class="afl-buy__th' +
      (on ? ' is-on' : '') +
      (blocked ? ' is-off' : '') +
      '" data-hc-el="variant-picker" data-pick-slot="' +
      slot +
      '" data-pick-index="' +
      i +
      '" role="radio" aria-checked="' +
      (on ? 'true' : 'false') +
      '"' +
      (blocked ? ' aria-disabled="true"' : '') +
      ' tabindex="' +
      (on ? '0' : '-1') +
      '"><span class="afl-buy__th-well"><img src="' +
      esc(d.packSrc) +
      '" srcset="' +
      esc(d.packSrcset) +
      '" alt="" loading="lazy" decoding="async" width="48" height="48"></span><span class="afl-buy__th-n">' +
      // 06.09: the status dot rides the name so in-stock vs pre-order reads
      // BEFORE she picks (assets/afroyla-seen-detail-sheet.js reads this
      // span's textContent for the name — an empty <i> adds no text).
      dotHtml(d.preorder) +
      label +
      '</span>' +
      price +
      '</button>'
    );
  };

  /* What taking tile `i` in `slot` adds to the payable: the chosen units
     priced with it, minus the same units priced without that slot. */
  Box.prototype.marginalCents = function (slot, i) {
    var self = this;
    var sum = function (indices) {
      return self
        .priceBackpacks(
          indices.map(function (k) {
            return self.designs[k];
          })
        )
        .reduce(function (s, line) {
          return s + line.payCents;
        }, 0);
    };
    var picks = this.picks.slice(0, Math.max(this.units, slot + 1));
    var withIt = picks.slice();
    withIt[slot] = i;
    var without = picks.slice();
    without.splice(slot, 1);
    return sum(withIt) - sum(without);
  };

  /* v13 — the group tab a rail shows: the one opened by hand, else the group
     of the slot's own pick (a form belongs to its design's group), else the
     first. Empty when the blocks carry fewer than two groups. */
  Box.prototype.tabFor = function (slot) {
    if (this.groups.length < 2) return '';
    var key = 'p' + slot;
    var byHand = this.tabs[key];
    if (
      byHand &&
      this.groups.some(function (g) {
        return g.key === byHand;
      })
    ) {
      return byHand;
    }
    var d = this.designs[this.picks[slot]];
    return d && d.group ? d.group : this.groups[0].key;
  };

  /* The designs (never the forms) a rail lists: the open tab's, plus any
     design with no group at all. */
  Box.prototype.designsFor = function (slot) {
    var tab = this.tabFor(slot);
    var out = [];
    this.designs.forEach(function (d, i) {
      if (d.isForm) return;
      if (tab && d.group && d.group !== tab) return;
      out.push(i);
    });
    return out;
  };

  /* From Piece 2 the designs sit behind the "Another handbag" tile — revealed
     by hand, or because the slot already holds a design rather than a form. */
  Box.prototype.moreOpen = function (slot) {
    var d = this.designs[this.picks[slot]];
    return !!this.more['p' + slot] || !(d && d.isForm);
  };

  Box.prototype.tabsHtml = function (slot) {
    var self = this;
    if (this.groups.length < 2) return '';
    if (slot > 0 && this.families && !this.moreOpen(slot)) return '';
    var tab = this.tabFor(slot);
    return (
      '<div class="afl-buy__tabs" role="tablist">' +
      this.groups
        .map(function (g) {
          var on = g.key === tab;
          return (
            '<button type="button" class="afl-buy__tab' +
            (on ? ' is-on' : '') +
            '" role="tab" aria-selected="' +
            (on ? 'true' : 'false') +
            '" data-buy-tab="p' +
            slot +
            '" data-group="' +
            esc(g.key) +
            '" data-hc-el="tab">' +
            esc(g.label) +
            '</button>'
          );
        })
        .join('') +
      '</div>'
    );
  };

  Box.prototype.thumbsHtml = function (slot) {
    var self = this;
    var html = '';
    var family = slot > 0 && this.families;
    if (family) {
      // the chosen design's other forms first, then the divider tile
      this.formsOf(this.picks[0]).forEach(function (i) {
        html += self.tileHtml(slot, i, true);
      });
      var open = this.moreOpen(slot);
      html +=
        '<button type="button" class="afl-buy__more' +
        (open ? ' is-open' : '') +
        '" data-buy-more="' +
        slot +
        '" aria-expanded="' +
        (open ? 'true' : 'false') +
        '" data-hc-el="accordion"><span class="afl-buy__more-well" aria-hidden="true"></span><span class="afl-buy__th-n">' +
        esc(this.root.getAttribute('data-more-label') || 'Another handbag') +
        '</span></button>';
      if (!open) return html;
    }
    this.designsFor(slot).forEach(function (i) {
      html += self.tileHtml(slot, i, family);
    });
    return html;
  };

  /* The chosen units, collapsed to one entry per design with its count — the
     same design twice has one ship window and one caption, said once. */
  Box.prototype.groupedChosen = function (units) {
    var groups = [];
    this.chosen(units).forEach(function (design) {
      var found = groups.filter(function (g) {
        return g.design === design;
      })[0];
      if (found) found.count += 1;
      else groups.push({ design: design, count: 1 });
    });
    return groups;
  };

  /* "Kora" once, "Kora ×2" when the shopper took two of it. */
  Box.prototype.nameFor = function (design, count) {
    if (!count || count < 2) return design.name;
    return fill(this.root.getAttribute('data-dup-name') || '{name} ×{n}', { name: design.name, n: count });
  };

  /* 06.09 (owner: "show rõ in stock / preorder"): the line states the STATUS
     first, then the name and its window — "Pre-order · Kora ships Sep 29 –
     Oct 6" / "In stock · Kora ships now". An in-stock backpack never reads
     "Pre-order" (the old line assumed every design was one). */
  /* 06.09 (owner 19:23: "charm có pre-order đâu … nghiên cứu kỹ phần đánh
     dấu preorder") — ONE status phrase for every piece the box sells, read
     from the piece's own data-preorder / data-window (snippets/
     afroyla-seen-ship-attrs, the PDP truth): "Pre-order · ships Sep 29 –
     Oct 6" / "Pre-order" (no dates yet) / "In stock · ships now". */
  Box.prototype.statusTextFor = function (item) {
    if (!item.preorder) return this.root.getAttribute('data-ship-now') || 'In stock · ships now';
    if (!item.window) return this.root.getAttribute('data-no-dates') || 'Pre-order';
    return fill(this.root.getAttribute('data-ship-pre') || 'Pre-order · ships {window}', { window: item.window });
  };

  /* "Kora — Pre-order · ships Sep 29 – Oct 6": the name, then its status. */
  Box.prototype.shipTextFor = function (design, count) {
    return this.nameFor(design, count) + ' — ' + this.statusTextFor(design);
  };

  /* The same line with the status DOT in front (gold = pre-order, green =
     in stock — the PDP's afroyla-ship-dot vocabulary). */
  Box.prototype.shipHtmlFor = function (design, count) {
    return dotHtml(design.preorder) + esc(this.shipTextFor(design, count));
  };

  function dotHtml(preorder) {
    return '<i class="afl-buy__dot afl-buy__dot--' + (preorder ? 'pre' : 'now') + '" aria-hidden="true"></i>';
  }

  Box.prototype.picksHtml = function () {
    var self = this;
    var slotTpl = this.root.getAttribute('data-slot-label') || 'Backpack {n}';
    var lowLabel = this.root.getAttribute('data-low-label') || '';
    var html = '';
    for (var slot = 0; slot < this.units; slot += 1) {
      var design = this.designs[this.picks[slot]];
      var label = fill(slotTpl, { n: slot + 1 });
      // "12 LEFT" — the review-tag style after the ship line, only at or under
      // the threshold, printing the DOM's own remaining count.
      var low =
        design.remaining !== null && design.remaining > 0 && design.remaining <= this.lowThreshold
          ? ' <span class="afl-buy__low">' + esc(design.remaining + ' ' + lowLabel) + '</span>'
          : '';
      var folded = this.isFolded('p' + slot, slot > 0);
      if (folded) label = fill(this.root.getAttribute('data-slot-label-compact') || 'Backpack {n}', { n: slot + 1 });
      html +=
        '<div class="afl-buy__pick' +
        (folded ? ' is-compact' : '') +
        '"><span class="afl-seen__label afl-buy__pick-lbl">' +
        esc(label) +
        '</span>' +
        (folded ? this.chosenHtml('p' + slot, design.packSrc, design.packSrcset, design.name, this.shipHtmlFor(design, 1).replace(esc(design.name) + ' — ', '') + low, this.viewButtonHtml()) : '') +
        // v13 — the group tab chips head an open rail (nothing without groups)
        (folded ? '' : this.tabsHtml(slot)) +
        '<div class="afl-buy__pick-thumbs" role="radiogroup" aria-label="' +
        esc(label) +
        '"' +
        (folded ? ' hidden' : '') +
        '>' +
        self.thumbsHtml(slot) +
        '</div>' +
        (folded ? '' : '<p class="afl-buy__pick-ship">' + this.shipHtmlFor(design, 1) + low + '</p>' + this.viewButtonHtml()) +
        '</div>';
    }
    return html;
  };

  /* A row is folded when compact mode is on, the row is foldable (never
     Backpack 1) and the shopper has not tapped Change on it. */
  Box.prototype.isFolded = function (key, foldable) {
    return this.compact && foldable && !this.open[key];
  };

  /* The one-line "chosen" row: thumb · name + status · Change. The rail it
     replaces stays in the DOM (hidden) so the sticky bar and the detail
     sheet keep reading the ticked thumb. */
  Box.prototype.chosenHtml = function (key, src, srcset, name, statusHtml, viewHtml) {
    var change = this.root.getAttribute('data-change-label') || 'Change ›';
    return (
      '<div class="afl-buy__chosen"><span class="afl-buy__chosen-well"><img src="' +
      esc(src) +
      '" srcset="' +
      esc(srcset) +
      '" alt="" loading="lazy" decoding="async" width="56" height="56"></span><span class="afl-buy__chosen-main"><b class="afl-buy__chosen-name">' +
      esc(name) +
      '</b><span class="afl-buy__chosen-ship">' +
      statusHtml +
      '</span></span><span class="afl-buy__chosen-acts"><button type="button" class="afl-buy__change" data-hc-el="x-pick-change" data-buy-change="' +
      esc(key) +
      '">' +
      esc(change) +
      '</button>' +
      (viewHtml || '') +
      '</span></div>'
    );
  };

  Box.prototype.paintCards = function () {
    var self = this;
    var saveTpl = this.root.getAttribute('data-badge-save') || 'Save {save}';
    var totalTpl = this.root.getAttribute('data-total-label') || '{total} total';
    var priced = this.cards.map(function (card) {
      var units = toInt(card.getAttribute('data-units'));
      var listTotal = 0;
      var payTotal = 0;
      self.priceBackpacks(self.chosen(units)).forEach(function (line) {
        listTotal += line.listCents;
        payTotal += line.payCents;
      });
      // v12 — the open gifts join the struck total (what she receives), pay $0.
      var giftList = self.giftListCents(units);
      return { card: card, units: units, listTotal: listTotal, payTotal: payTotal, giftList: giftList };
    });
    if (!priced.length) return;
    var giftMetaTpl = this.root.getAttribute('data-gift-meta') || '';

    priced.forEach(function (entry) {
      var card = entry.card;
      var pay = card.querySelector('[data-card-pay]');
      var list = card.querySelector('[data-card-list]');
      // v12.7 (owner 06.09 01:40: a fifth-grader must get it at a glance): the
      // big figure is what she PAYS for the whole set; under it "for 2
      // backpacks · $85.98 each"; the struck figure is the backpacks' list;
      // SAVE counts the backpacks only — the gifts state their own worth in
      // the strip below.
      var u = Math.max(1, entry.units);
      if (pay) pay.textContent = money(entry.payTotal, self.fmt);
      if (list) list.textContent = money(entry.listTotal, self.fmt);
      var each = card.querySelector('[data-card-each]');
      if (each) each.hidden = true;
      var totalEl = card.querySelector('[data-card-total]');
      if (totalEl) {
        var forTpl = u < 2 ? self.root.getAttribute('data-for-one') || 'for 1 backpack' : self.root.getAttribute('data-for-many') || 'for {n} backpacks · {each} each';
        totalEl.textContent = fill(forTpl, { n: u, each: money(Math.round(entry.payTotal / u), self.fmt) });
        totalEl.hidden = false;
      }

      // v11 — the badge above the title is a merchant string rendered by
      // Liquid and carries no figure; the saving under the struck price is a
      // figure and carries no claim, so neither can lie about the other. A
      // card that saves nothing shows no line, so it can never say "$0.00".
      var saveCents = entry.listTotal - entry.payTotal;
      var saveEl = card.querySelector('[data-card-save]');
      // "· charm + wallet on us" after the count — the gifts this tier opens.
      var metaGift = card.querySelector('[data-card-gift-meta]');
      if (metaGift) {
        var giftNames = self.giftNamesText(entry.units);
        metaGift.textContent = giftNames && giftMetaTpl ? fill(giftMetaTpl, { gifts: giftNames }) : '';
      }
      if (saveEl) {
        saveEl.textContent = fill(saveTpl, { save: money(saveCents, self.fmt) });
        saveEl.hidden = saveCents <= 0;
      }

      var on = entry.units === self.units;
      card.classList.toggle('is-on', on);
      if (!on) card.classList.remove('is-pending');
      // The wrapper's aria-checked is the hook afroyla-seen-sticky.js reads.
      card.setAttribute('aria-checked', on ? 'true' : 'false');

      // The ROW is the radio: it owns the roving tabindex and the expansion.
      var row = card.querySelector('[data-buy-row]');
      if (row) {
        row.setAttribute('aria-checked', on ? 'true' : 'false');
        row.setAttribute('aria-expanded', on ? 'true' : 'false');
        row.tabIndex = on ? 0 : -1;
      }

      // Collapsed = out of the tab order AND out of the accessibility tree.
      var body = card.querySelector('.afl-buy__body');
      if (body) body.setAttribute('aria-hidden', on ? 'false' : 'true');
      var bodyIn = card.querySelector('.afl-buy__body-in');
      if (bodyIn) {
        if (on) bodyIn.removeAttribute('inert');
        else bodyIn.setAttribute('inert', '');
      }

      var picks = card.querySelector('[data-card-picks]');
      if (picks) picks.innerHTML = on ? self.picksHtml() : '';
      var giftRows = card.querySelector('[data-card-gifts]');
      if (giftRows) giftRows.innerHTML = on ? self.giftRowsHtml() : '';
      // v12.7 — the chosen backpacks as tiles in the row ("1 backpack" made visible)
      var bags = card.querySelector('[data-card-bags]');
      if (bags) bags.innerHTML = self.rowBagsHtml(entry.units);
      // v12.7 — the chosen backpacks as tiles in the row ("1 backpack" made visible)
      var bags = card.querySelector('[data-card-bags]');
      if (bags) bags.innerHTML = self.rowBagsHtml(entry.units);
      // the mystery strip in the row: sealed boxes, or the revealed pieces
      var mg = card.querySelector('[data-card-mg]');
      if (mg) mg.innerHTML = self.rowGiftsHtml(entry.units, on, on && self.revealing);
    });
  };

  /* The gallery shows exactly one design — the active one. The active design
     must be one of the chosen ones, so a card change falls back to Backpack 1. */
  Box.prototype.activeIndex = function () {
    var visible = this.picks.slice(0, this.units);
    return visible.indexOf(this.active) >= 0 ? this.active : visible[0];
  };

  /* v12.9 — fills the shared gift gallery with one gift option's photos
     (main frame = photo 1, one thumb per photo, same data-buy-thumb path as a
     backpack's photos). Skipped when the same gift is already on show, so a
     re-paint does not reset the photo the shopper is looking at. */
  Box.prototype.fillGiftGallery = function (giftIndex) {
    var gal = this.giftGallery;
    if (!gal) return;
    var line = this.giftLines().filter(function (g) {
      return g.index === giftIndex;
    })[0];
    if (!line) return;
    var key = giftIndex + ':' + line.option.variantId;
    if (this.paintedGift === key) return;
    this.paintedGift = key;
    var opt = line.option;
    var mains = opt.images.length ? opt.images : [opt.src.replace(/width=\d+/, 'width=900')];
    var thumbs = opt.thumbs.length ? opt.thumbs : [opt.src.replace(/width=\d+/, 'width=400')];
    var label = (this.root.getAttribute('data-free-tag') || 'Free') + ' ' + line.name + ' — ' + opt.name;
    var main = gal.querySelector('[data-buy-main]');
    if (main) {
      main.src = mains[0];
      main.srcset = '';
      main.alt = label;
    }
    var strip = gal.querySelector('[data-gift-thumbs]');
    if (strip) {
      strip.innerHTML = mains
        .map(function (src, i) {
          return (
            '<button type="button" class="afl-buy__thumb" data-buy-thumb data-hc-el="gallery-thumb" data-main-src="' +
            esc(src) +
            '" data-main-srcset="" data-main-alt="' +
            esc(label + ' — photo ' + (i + 1)) +
            '" aria-label="' +
            esc(label + ' — photo ' + (i + 1) + ' of ' + mains.length) +
            '" aria-current="' +
            (i === 0 ? 'true' : 'false') +
            '"><img src="' +
            esc(thumbs[i] || thumbs[0]) +
            '" alt="" loading="lazy" decoding="async" width="400" height="400"></button>'
          );
        })
        .join('');
    }
  };

  /* v12.9 — a tapped FREE piece takes the gallery (owner 06.09 13:34: tapping
     a gift only swapped the big photo, and on a phone the big photo is
     hidden, so nothing seemed to happen). */
  Box.prototype.showGift = function (giftIndex) {
    if (giftIndex < 0 || giftIndex >= this.units || !this.giftGallery) return;
    this.activeGift = giftIndex;
    this.paintGallery();
  };

  Box.prototype.paintGallery = function () {
    var self = this;
    var active = this.activeIndex();
    this.active = active;
    // a gift stays on show only while its tier is still included
    if (this.activeGift != null && this.activeGift >= this.units) this.activeGift = null;
    var giftOnShow = this.activeGift != null && !!this.giftGallery;

    var activeGallery = null;
    this.designs.forEach(function (d, i) {
      var show = i === active;
      d.el.setAttribute('data-active', show ? 'true' : 'false');
      if (d.gallery) {
        d.gallery.hidden = !show || giftOnShow;
        if (show) activeGallery = d.gallery;
      }
    });
    if (this.giftGallery) {
      if (giftOnShow) {
        this.fillGiftGallery(this.activeGift);
        activeGallery = this.giftGallery;
      } else {
        this.paintedGift = null;
      }
      this.giftGallery.hidden = !giftOnShow;
    }

    if (!this.setStrip) return;

    /* The strip is "your set": the chosen backpacks (tap one → the gallery
       switches design) and, v12.9 (owner 06.09 13:16: "phần gallery đang chưa
       có sản phẩm được tặng… thể hiện rõ mấy cái được FREE"), the free pieces
       of the selected tier — the picked charm / wallet / circle bag with a
       FREE stamp. A gift tile carries data-buy-thumb, so tapping it puts the
       gift's packshot in the main frame through the same path as a photo. */
    var groups = this.groupedChosen();
    var gifts = this.giftLines();
    if (!activeGallery || groups.length + gifts.length < 2) {
      this.setStrip.hidden = true;
      this.setStrip.innerHTML = '';
      return;
    }

    var freeTag = this.root.getAttribute('data-free-tag') || 'Free';
    var designItems = groups.map(function (group) {
      var index = self.designs.indexOf(group.design);
      var on = index === active && !giftOnShow;
      return (
        '<button type="button" data-hc-el="gallery-thumb" class="afl-buy__set-item' +
        (on ? ' is-on' : '') +
        '" data-set-index="' +
        index +
        '" aria-current="' +
        (on ? 'true' : 'false') +
        '"><span class="afl-buy__set-well"><img src="' +
        esc(group.design.heroSrc) +
        '" alt="" loading="lazy" decoding="async"></span><span class="afl-buy__set-cap">' +
        esc(self.nameFor(group.design, group.count)) +
        '</span></button>'
      );
    });
    var giftItems = gifts.map(function (g) {
      var kind = g.name ? g.name.charAt(0).toUpperCase() + g.name.slice(1) : '';
      var giftOn = giftOnShow && self.activeGift === g.index;
      return (
        '<button type="button" data-hc-el="gallery-thumb" class="afl-buy__set-item afl-buy__set-item--gift' +
        (giftOn ? ' is-on' : '') +
        '" data-set-gift="' +
        g.index +
        '" aria-label="' +
        esc(freeTag + ' ' + g.name + ' — ' + g.option.name) +
        '" aria-current="' +
        (giftOn ? 'true' : 'false') +
        '"><span class="afl-buy__set-well"><img src="' +
        esc(g.option.src) +
        '" srcset="' +
        esc(g.option.srcset) +
        '" alt="" loading="lazy" decoding="async"></span><span class="afl-buy__set-cap"><span class="afl-buy__set-free">' +
        esc(freeTag) +
        '</span>' +
        esc(kind) +
        '</span></button>'
      );
    });
    this.setStrip.innerHTML = designItems.concat(giftItems).join('');
    this.setStrip.hidden = false;

    // Sit under the main frame of the gallery that is actually on screen.
    var thumbs = activeGallery.querySelector('.afl-buy__thumbs');
    if (thumbs) activeGallery.insertBefore(this.setStrip, thumbs);
    else activeGallery.appendChild(this.setStrip);

  };

  Box.prototype.paintCompanions = function () {
    var pct = this.companionPct();
    var self = this;
    this.companions.forEach(function (input) {
      var row = input.closest('.afl-buy__comp');
      var out = row && row.querySelector('[data-comp-pay]');
      if (!out) return;
      var listCents = toInt(input.getAttribute('data-list-cents'));
      out.textContent = money(listCents - Math.floor((listCents * pct) / 100), self.fmt);
    });
  };

  /* v8.8: everything the summary used to say is gone. What is left below the
     cards is the button — plus two invisible hooks that carry the payable and
     the saving, because sections/afroyla-seen-sticky.liquid reads them out of
     this DOM to build its own bar and that file is not ours to touch. */
  Box.prototype.paintTotals = function () {
    var priced = this.priceBackpacks(this.chosen());
    var companions = this.tickedCompanions();

    var listTotal = 0;
    var payTotal = 0;
    priced.forEach(function (line) {
      listTotal += line.listCents;
      payTotal += line.payCents;
    });
    companions.forEach(function (c) {
      listTotal += c.listCents;
      payTotal += c.payCents;
    });
    // v12 — the open gifts: list value counted, nothing paid, one unit each in
    // the permalink so the discount function can zero them in the cart.
    // v12.7: the saving the sticky bar quotes = the backpacks only, the same
    // figure the card prints; the gifts state their own worth in the strip.
    var gifts = this.giftLines();

    var total = this.root.querySelector('[data-buy-total]');
    if (total) total.textContent = money(payTotal, this.fmt);
    var save = this.root.querySelector('[data-buy-save]');
    if (save) save.textContent = money(listTotal - payTotal, this.fmt);

    if (this.cta) {
      // Same design twice is ONE permalink entry with quantity 2 — repeating an
      // id in /cart/{id}:1,{id}:1 is not honoured reliably by Shopify.
      var order = [];
      var qtyById = {};
      priced
        .map(function (line) {
          return line.design.variantId;
        })
        .concat(
          companions.map(function (c) {
            return c.variantId;
          })
        )
        .concat(
          gifts.map(function (g) {
            return g.option.variantId;
          })
        )
        .filter(function (id) {
          return id > 0;
        })
        .forEach(function (id) {
          if (qtyById[id]) qtyById[id] += 1;
          else {
            qtyById[id] = 1;
            order.push(id);
          }
        });
      var ids = order.map(function (id) {
        return id + ':' + qtyById[id];
      });
      // 06.09 (owner picked "N5"): the button names the win, never a design
      // name (a shopper may not know who "Kora" is) — one backpack: "Claim
      // mine + the free charm", two or three: "Claim my set + 2 free gifts".
      // {gift} = the last word of the tier-1 gift kind ("leather charm" →
      // charm), {n} = open gifts. The tap is a cart permalink straight to
      // checkout, so the line under the button says so.
      var giftCount = gifts.length;
      var giftWord = giftCount ? String(gifts[0].name || '').trim().split(/\s+/).pop() : '';
      var tokens = { n: String(giftCount), gift: giftWord, gifts: this.giftNamesText() };
      var oneTpl = this.root.getAttribute('data-cta-one') || 'Claim mine + the free {gift}';
      var manyTpl = this.root.getAttribute('data-cta-many') || 'Claim my set + {n} free gifts';
      var label = fill(this.units > 1 ? manyTpl : oneTpl, tokens);
      // a gift tier with an empty pool leaves no "+ the free …" to promise
      if (!giftCount) label = label.split(' + ')[0];
      // two lines by design (label wrapped at 390 with the total inline):
      // Druk label over a small Sweet Sans total; the sticky bar copies both spans
      this.cta.innerHTML = '<span class="afl-buy__btn-l">' + esc(label) + '</span><span class="afl-buy__btn-t">' + esc(money(payTotal, this.fmt)) + '</span>';
      var fine = this.root.querySelector('[data-buy-fine]');
      if (fine) {
        var fineTpl = this.root.getAttribute(this.units > 1 ? 'data-fine-many' : 'data-fine-one') || '';
        fine.textContent = giftCount ? fill(fineTpl, tokens) : this.root.getAttribute('data-fine-none') || '';
        fine.hidden = !fine.textContent;
      }
      // v13 — the LP key rides along as the cart attribute the discount
      // function gates on, after the ab attributes when there are any.
      var attrs = abAttributes();
      if (this.lpKey) attrs._afl_lp = this.lpKey;
      // the same set for the Cart API path (checkoutViaCart); null = nothing to buy
      this.checkoutSet = ids.length
        ? {
            items: order.map(function (id) {
              return { id: id, quantity: qtyById[id] };
            }),
            attributes: attrs,
          }
        : null;
      this.cta.setAttribute('href', ids.length ? '/cart/' + ids.join(',') + attributesQuery(attrs) : '/cart');
    }
  };

  /* 07.09 — the tap on the checkout button (or the sticky bar's copy of it):
     rebuild the cart through the Cart API so afroyla-preorder-cart.js can
     attach the pre-order properties to every pre-order line, then go to
     checkout. Returns false when the permalink must be followed instead. */
  Box.prototype.checkoutViaCart = function (fallbackHref) {
    var set = this.checkoutSet;
    if (!set || !window.fetch) return false;
    if (this.checkoutBusy) return true; // a second tap while the first is in flight
    var self = this;
    this.checkoutBusy = true;
    if (this.cta) this.cta.setAttribute('aria-busy', 'true');
    cartPost('/cart/clear.js')
      .then(function () {
        var keys = Object.keys(set.attributes || {});
        return keys.length ? cartPost('/cart/update.js', { attributes: set.attributes }) : null;
      })
      .then(function () {
        return cartPost('/cart/add.js', { items: set.items });
      })
      .then(function () {
        window.location.href = '/checkout';
      })
      .catch(function () {
        self.checkoutBusy = false;
        if (self.cta) self.cta.removeAttribute('aria-busy');
        window.location.href = fallbackHref;
      });
    return true;
  };

  /* 06.09 — "What ships when" above the button: one row per ship box (in
     stock first, then each batch window by date) built from the chosen
     backpacks, the ticked companions and the open gifts, plus ONE note under
     it. The HTML comes from assets/afroyla-seen-ship-manifest.js (pure
     renderer); when that file is missing the block simply stays hidden. */
  Box.prototype.paintShip = function () {
    var out = this.root.querySelector('[data-buy-ship]');
    if (!out) return;
    var ship = window.AflSeenShip;
    if (!ship || typeof ship.html !== 'function') {
      out.hidden = true;
      return;
    }
    var self = this;
    var attr = function (name) {
      return self.root.getAttribute(name) || '';
    };
    // every piece with ITS OWN status: backpacks (grouped, "Kora ×2"), the
    // free gifts (named by kind — "Free leather charm + leather wallet"),
    // the ticked companions.
    var lines = this.groupedChosen(this.units).map(function (g) {
      return {
        name: self.nameFor(g.design, g.count),
        preorder: g.design.preorder,
        window: g.design.window,
        remaining: g.design.remaining,
        kind: 'backpack',
      };
    });
    // gifts that ship together are ONE item — "Free leather charm + leather
    // wallet" — never "Free charm, Free wallet"
    var freeTpl = attr('data-ship-free') || 'Free {gifts}';
    var giftGroups = [];
    this.giftLines().forEach(function (g) {
      var key = (g.option.preorder ? 'pre:' + g.option.window : 'now');
      var found = giftGroups.filter(function (x) {
        return x.key === key;
      })[0];
      if (found) found.kinds.push(g.name);
      else giftGroups.push({ key: key, kinds: [g.name], preorder: g.option.preorder, window: g.option.window });
    });
    giftGroups.forEach(function (x) {
      var kinds = x.kinds.length > 1 ? x.kinds.slice(0, -1).join(', ') + ' + ' + x.kinds[x.kinds.length - 1] : x.kinds[0];
      lines.push({ name: fill(freeTpl, { gifts: kinds }), preorder: x.preorder, window: x.window, remaining: null, kind: 'gift' });
    });
    this.tickedCompanions().forEach(function (c) {
      lines.push({ name: c.name, preorder: c.preorder, window: c.window, remaining: null, kind: 'companion' });
    });
    var html = ship.html(lines, {
      label: attr('data-ship-label'),
      nowLine: attr('data-ship-now'),
      preLine: attr('data-ship-pre'),
      preNoDates: attr('data-no-dates'),
      lowLabel: attr('data-low-label'),
      lowThreshold: this.lowThreshold,
      lowNote: attr('data-ship-low-note'),
      openNote: attr('data-ship-open-note'),
    });
    out.innerHTML = html;
    out.hidden = !html;
  };

  /* 06.09 (owner: "mobile … những chỗ cần slide") — below 990 the design and
     gift picker rows are sideways rails; after a paint, bring each row's
     chosen tile into view so the selection is never off-screen. */
  Box.prototype.revealPicked = function () {
    this.root.querySelectorAll('.afl-buy__pick-thumbs, .afl-buy__gift-thumbs, .afl-buy__tabs').forEach(function (rail) {
      if (rail.scrollWidth <= rail.clientWidth + 1) return;
      var on = rail.querySelector('.afl-buy__th.is-on, .afl-buy__tab.is-on');
      if (!on) return;
      var left = on.offsetLeft - rail.offsetLeft;
      if (left < rail.scrollLeft) rail.scrollLeft = Math.max(0, left - 8);
      else if (left + on.offsetWidth > rail.scrollLeft + rail.clientWidth) rail.scrollLeft = left + on.offsetWidth - rail.clientWidth + 8;
    });
  };

  Box.prototype.render = function () {
    this.paintRail();
    this.paintCards();
    this.paintGallery();
    this.paintCompanions();
    this.paintTotals();
    this.paintShip();
    this.revealPicked();
    if (this.revealing) this.fireReveal();
    // the unlock / reveal motion runs once, on the paint that changed the tier
    this.unlocking = [];
    this.revealing = false;
  };

  Box.prototype.setUnits = function (units) {
    var next = Math.min(MAX_UNITS, Math.max(1, units));
    if (next === this.units) return;
    // v12.1 — the newly selected card's sealed boxes reveal, whichever way she
    // moved. v12.9 (owner 06.09 13:10: "cái hộp quà khi click vào nó lại tự
    // biến thành cái khóa là sao"): the padlock-drop phase used to run on the
    // tiers that were LOCKED in the previously selected card — but the strip
    // she is looking at is the new card's own, where those slots were sealed
    // boxes, so a box turned into a padlock before opening. The drop is gone;
    // every included slot plays box → open. `unlocking` stays for the
    // confetti sizing but is always empty now.
    this.unlocking = [];
    this.revealing = true;
    this.units = next;
    // a new card starts the gallery on Backpack 1 again
    this.active = this.picks[0];
    this.activeGift = null;

    /* v12.3 (owner 22:03: "nguyên đoạn dài pop ra, bị anchor xuống, chưa kịp
       nhìn hiệu ứng"). Two fixes:
       1. the chosen ROW stays where it is on screen — the card above it may
          collapse and pull the page up, so the scroll is corrected by exactly
          that shift, instantly, before the eye can notice;
       2. the body waits: the row's gifts reveal first (~1.1s) while the card is
          `is-pending`, then the body opens. */
    var self = this;
    var target = this.cards.filter(function (c) {
      return toInt(c.getAttribute('data-units')) === next;
    })[0];
    var row = target && target.querySelector('[data-buy-row]');
    var before = row ? row.getBoundingClientRect().top : null;
    if (target) target.classList.add('is-pending');
    // The card that was open collapses INSTANTLY (`is-snap` kills its
    // transition): the shift it causes is then measurable in the same frame
    // and corrected before paint, so the chosen row never visibly moves.
    var prev = this.cards.filter(function (c) {
      return c.classList.contains('is-on') && c !== target;
    })[0];
    if (prev) prev.classList.add('is-snap');
    this.render();
    if (prev) {
      window.requestAnimationFrame(function () {
        prev.classList.remove('is-snap');
      });
    }
    if (row && before !== null) {
      // Correct whichever ancestor actually scrolls (this theme may scroll a
      // wrapper), synchronously now, then hold for a few frames as a safety.
      var scroller = row.parentElement;
      while (scroller && scroller !== document.body) {
        var oy = window.getComputedStyle(scroller).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && scroller.scrollHeight > scroller.clientHeight) break;
        scroller = scroller.parentElement;
      }
      if (!scroller || scroller === document.body) scroller = document.scrollingElement || document.documentElement;
      // The theme sets `scroll-behavior: smooth` on <html>, which would turn
      // this correction into a 500ms glide — switch it off for the hold.
      var htmlEl = document.documentElement;
      var savedHtmlSb = htmlEl.style.scrollBehavior;
      var savedSb = scroller.style.scrollBehavior;
      htmlEl.style.scrollBehavior = 'auto';
      scroller.style.scrollBehavior = 'auto';
      var shiftNow = row.getBoundingClientRect().top - before;
      if (Math.abs(shiftNow) > 0.5) scroller.scrollTop += shiftNow;
      var t0 = window.performance ? window.performance.now() : Date.now();
      var hold = function () {
        var drift = row.getBoundingClientRect().top - before;
        if (Math.abs(drift) > 0.5) scroller.scrollTop += drift;
        var now = window.performance ? window.performance.now() : Date.now();
        if (now - t0 < 380) window.requestAnimationFrame(hold);
        else {
          htmlEl.style.scrollBehavior = savedHtmlSb;
          scroller.style.scrollBehavior = savedSb;
        }
      };
      window.requestAnimationFrame(hold);
    }
    if (target) {
      window.clearTimeout(this.pendingTimer);
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.pendingTimer = window.setTimeout(function () {
        target.classList.remove('is-pending');
      }, reduce ? 0 : 1100);
    }
  };

  /* Only the gallery moves — the card and the picks are untouched. */
  Box.prototype.setActive = function (index) {
    if (!this.designs[index] || this.picks.slice(0, this.units).indexOf(index) < 0) return;
    if (this.active === index && this.activeGift == null) return;
    this.active = index;
    this.activeGift = null; // a backpack tile always takes the gallery back
    this.paintGallery();
  };

  /* Any row may hold any design, the same one included: the discount ranks
     UNITS, so two of one design price like two different designs. */
  Box.prototype.setPick = function (slot, index) {
    if (slot < 0 || slot >= MAX_UNITS || !this.designs[index]) return;
    if (this.picks[slot] === index) return;
    var prev = this.picks[slot];
    this.picks[slot] = index;
    // the row the shopper just touched becomes the design the gallery shows
    this.active = index;
    this.activeGift = null;
    delete this.open['p' + slot]; // a pick folds the row again
    // v13 — the rail's tab follows the pick's own group again; a new Piece 1
    // pulls its forms and its untouched gifts along with it
    if (this.designs[index].group) delete this.tabs['p' + slot];
    if (slot === 0) {
      if (this.families) this.followForms(prev, index);
      this.refreshGiftDefaults();
    }
    this.render();
  };

  /* ---- wiring ------------------------------------------------------------- */
  var boxes = [];

  function boxOf(node) {
    var root = node.closest('[data-afl-buy]');
    return boxes.filter(function (b) {
      return b.root === root;
    })[0];
  }

  function swapMain(thumb) {
    var gallery = thumb.closest('[data-buy-gallery]');
    if (!gallery) return;
    var main = gallery.querySelector('[data-buy-main]');
    if (main) {
      main.src = thumb.getAttribute('data-main-src') || main.src;
      main.srcset = thumb.getAttribute('data-main-srcset') || '';
      main.alt = thumb.getAttribute('data-main-alt') || '';
    }
    gallery.querySelectorAll('[data-buy-thumb]').forEach(function (other) {
      other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
    });
  }

  /* Bubble phase, after the sticky bar's own handler: a click it already
     cancelled (scroll-to-box state) must not start a checkout. Any anchor whose
     href equals a box's permalink — the box button or the sticky bar's copy —
     is that box's checkout. */
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || !event.target.closest) return;
    var link = event.target.closest('a[href^="/cart/"]');
    if (!link) return;
    var href = link.getAttribute('href');
    for (var i = 0; i < boxes.length; i += 1) {
      var box = boxes[i];
      if (!box.cta || box.cta.getAttribute('href') !== href) continue;
      if (box.checkoutViaCart(href)) event.preventDefault();
      return;
    }
  });

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var th = event.target.closest('.afl-buy__th');
    if (th) {
      event.preventDefault();
      if (th.getAttribute('aria-disabled') === 'true') return;
      var thBox = boxOf(th);
      if (!thBox) return;
      // v12 — a gift thumb picks that tier's piece; a backpack thumb a design
      if (th.hasAttribute('data-gift-tier')) thBox.setGift(toInt(th.getAttribute('data-gift-tier')), toInt(th.getAttribute('data-gift-index')));
      else thBox.setPick(toInt(th.getAttribute('data-pick-slot')), toInt(th.getAttribute('data-pick-index')));
      return;
    }

    // 06.09 — "Change ›" on a folded row opens that row's rail in place
    var change = event.target.closest('[data-buy-change]');
    if (change) {
      event.preventDefault();
      var changeBox = boxOf(change);
      if (changeBox) {
        changeBox.open[change.getAttribute('data-buy-change')] = true;
        changeBox.render();
      }
      return;
    }

    // v13 — a group tab filters the rail it heads
    var tab = event.target.closest('[data-buy-tab]');
    if (tab) {
      event.preventDefault();
      var tabBox = boxOf(tab);
      if (tabBox) {
        tabBox.tabs[tab.getAttribute('data-buy-tab')] = tab.getAttribute('data-group');
        tabBox.render();
      }
      return;
    }

    // v13 — "Another handbag" reveals (or folds) the designs after the forms
    var more = event.target.closest('[data-buy-more]');
    if (more) {
      event.preventDefault();
      var moreBox = boxOf(more);
      if (moreBox) {
        var moreKey = 'p' + more.getAttribute('data-buy-more');
        moreBox.more[moreKey] = !moreBox.more[moreKey];
        moreBox.render();
      }
      return;
    }

    // v13 — a page link (href="#seen-buy" data-hb-group="faith") opens that
    // group's tab on Piece 1; the link's own href does the scrolling
    var hbLink = event.target.closest('[data-hb-group]');
    if (hbLink) {
      var hbGroup = (hbLink.getAttribute('data-hb-group') || '').trim().toLowerCase();
      boxes.forEach(function (b) {
        if (
          b.groups.some(function (g) {
            return g.key === hbGroup;
          })
        ) {
          b.tabs.p0 = hbGroup;
          b.render();
        }
      });
    }

    // v12.9 — a FREE piece in the "your set" strip opens its own gallery
    var giftTile = event.target.closest('[data-set-gift]');
    if (giftTile) {
      event.preventDefault();
      var giftBox = boxOf(giftTile);
      if (giftBox) giftBox.showGift(toInt(giftTile.getAttribute('data-set-gift')));
      return;
    }

    var setItem = event.target.closest('[data-set-index]');
    if (setItem) {
      event.preventDefault();
      var setBox = boxOf(setItem);
      if (setBox) setBox.setActive(toInt(setItem.getAttribute('data-set-index')));
      return;
    }

    var photo = event.target.closest('[data-buy-thumb]');
    if (photo) {
      event.preventDefault();
      swapMain(photo);
      return;
    }

    // Tapping anywhere on the collapsed row selects — the count, the label
    // and the price are all inside the button. Copy inside an OPEN card is
    // not: it is a sibling of the row, so reading it selects nothing.
    var row = event.target.closest('[data-buy-row]');
    if (row) {
      var card = row.closest('[data-buy-card]');
      var cardBox = card && boxOf(card);
      if (cardBox) cardBox.setUnits(toInt(card.getAttribute('data-units')));
    }
  });

  document.addEventListener('change', function (event) {
    if (!event.target.closest) return;
    var input = event.target.closest('[data-buy-companion]');
    if (!input) return;
    var box = boxOf(input);
    if (box) box.render();
  });

  document.addEventListener('keydown', function (event) {
    if (!event.target.closest) return;

    // The thumbs sit INSIDE the selected card, so they are tested first: an
    // arrow key in a picker row moves the design, never the tier.
    var th = event.target.closest('.afl-buy__th');
    if (!th) {
      var tierRow = event.target.closest('[data-buy-row]');
      if (!tierRow) return;
      var card = tierRow.closest('[data-buy-card]');
      var box = card && boxOf(card);
      if (!box) return;
      var units = toInt(card.getAttribute('data-units'));
      var next = 0;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (units % MAX_UNITS) + 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = ((units + 1) % MAX_UNITS) + 1;
      else if (event.key === ' ' || event.key === 'Enter') next = units;
      if (!next) return;
      event.preventDefault();
      box.setUnits(next);
      var target = box.cards.filter(function (c) {
        return toInt(c.getAttribute('data-units')) === next;
      })[0];
      var targetRow = target && target.querySelector('[data-buy-row]');
      if (targetRow) targetRow.focus();
      return;
    }

    var thBox = boxOf(th);
    if (!thBox) return;

    // v12 — arrows inside a GIFT row move that tier's piece
    var giftRow = th.closest('.afl-buy__gift-thumbs');
    if (giftRow) {
      var gAll = Array.prototype.slice.call(giftRow.querySelectorAll('.afl-buy__th'));
      var gAt = gAll.indexOf(th);
      var gStep = 0;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') gStep = 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') gStep = -1;
      if (!gStep) return;
      event.preventDefault();
      var gTier = toInt(th.getAttribute('data-gift-tier'));
      for (var gHop = 1; gHop <= gAll.length; gHop += 1) {
        var gNext = gAll[(((gAt + gStep * gHop) % gAll.length) + gAll.length) % gAll.length];
        if (gNext && gNext.getAttribute('aria-disabled') !== 'true') {
          var gIndex = gNext.getAttribute('data-gift-index');
          thBox.setGift(gTier, toInt(gIndex));
          var gAgain = thBox.root.querySelector('.afl-buy__th[data-gift-tier="' + gTier + '"][data-gift-index="' + gIndex + '"]');
          if (gAgain) gAgain.focus();
          return;
        }
      }
      return;
    }

    var row = th.closest('.afl-buy__pick-thumbs');
    if (!row) return;
    var all = Array.prototype.slice.call(row.querySelectorAll('.afl-buy__th'));
    var at = all.indexOf(th);
    var step = 0;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') step = 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') step = -1;
    if (!step) return;
    event.preventDefault();
    var slot = toInt(th.getAttribute('data-pick-slot'));
    for (var hop = 1; hop <= all.length; hop += 1) {
      var at2 = (((at + step * hop) % all.length) + all.length) % all.length;
      var candidate = all[at2];
      if (candidate && candidate.getAttribute('aria-disabled') !== 'true') {
        var index = candidate.getAttribute('data-pick-index');
        thBox.setPick(slot, toInt(index));
        var again = thBox.root.querySelector(
          '.afl-buy__th[data-pick-slot="' + slot + '"][data-pick-index="' + index + '"]'
        );
        if (again) again.focus();
        return;
      }
    }
  });

  function boot() {
    document.querySelectorAll('[data-afl-buy]').forEach(function (root) {
      if (!root.querySelector('[data-buy-design]')) return;
      var box = new Box(root);
      boxes.push(box);
      box.render();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
